export const enum TokenType {
  DOUBLE_QUOTE,
  DOT,
  OTHER,
}

export interface Token {
  type: TokenType;
  value: string;
}

const ESCAPE_CHARACTER = '\\';

/**
 * Computes accessor parts.
 *
 * @param accessor - Accessor to the values
 * @returns List of accessors parts
 */
export function getAccessorParts(accessor: string): string[] {
  let currentPart = '';
  let openedQuote = false;
  const accessorParts: string[] = [];

  for (const token of tokenize(accessor)) {
    if (token.type === TokenType.DOUBLE_QUOTE) {
      openedQuote = !openedQuote;
    } else if (openedQuote) {
      currentPart += token.value;
    } else if (token.type === TokenType.DOT) {
      accessorParts.push(currentPart);
      currentPart = '';
    } else {
      currentPart += token.value;
    }
  }

  if (currentPart.length > 0) {
    accessorParts.push(currentPart);
  }

  return accessorParts;
}

/**
 * Generates tokens from the string provided taking into account escape sequences.
 *
 * @param value - Value that has to be tokenized
 * @returns Generator of type token from the value passed
 */
export function* tokenize(value: string): Generator<Token> {
  let escaping = false;

  for (const chr of value) {
    if (escaping) {
      escaping = false;
      yield buildToken(chr, true);
    } else if (chr === ESCAPE_CHARACTER) {
      escaping = true;
    } else {
      yield buildToken(chr);
    }
  }

  if (escaping) {
    throw new Error(`Unexpected escape sequence at index ${value.length - 1}`);
  }
}

/**
 * Gets the root of the axis accesor.
 *
 * @param accessor - Accessor to the values
 * @returns Root of the axis accessor
 */
export function getAxisRootAccessor(accessor: string): string {
  const parts = getAccessorParts(accessor);

  return parts[0];
}

/**
 * Gets the extension of the axis accesor.
 *
 * @param accessor - Accessor to the values
 * @returns Extension of the axis accessor
 */
export function getAxisExtensionAccessor(accessor: string): string {
  const parts = getAccessorParts(accessor);

  return parts[1];
}

const buildToken = (chr: string, escaped: boolean = false): Token => {
  const tokenType = escaped ? TokenType.OTHER : getTokenType(chr);

  return {
    type: tokenType,
    value: chr,
  };
};

const getTokenType = (chr: string): TokenType => {
  switch (chr) {
    case '.':
      return TokenType.DOT;
    case '"':
      return TokenType.DOUBLE_QUOTE;
    default:
      return TokenType.OTHER;
  }
};
